import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { StoreHandler } from '../../../stores/StoreHandler';
import { useTimetableVersionLessonDataQuery } from '../../../types/planung-graphql-client-defs';
import { versionLessonRowToTableType } from '../utils/versionLessonRowToTableType';
import { TimetableVersionLessonStore } from './TimetableVersionLessonStore';

const LessonStoreContext = createContext<TimetableVersionLessonStore>({} as TimetableVersionLessonStore);
const timetableVersionLessonStoreHandler = new StoreHandler<TimetableVersionLessonStore>();

export const TimetableVersionLessonProvider = ({ children }: { children: ReactNode | undefined }) => {
  const { versionUuid } = useParams();

  const store = timetableVersionLessonStoreHandler.register(versionUuid ?? v4(), () => {
    return new TimetableVersionLessonStore();
  });

  // Lazy: only load when the Lessons tab has been opened (ensureLessonsLoaded triggers this)
  const [lessonTabOpened, setLessonTabOpened] = useState(false);

  useEffect(() => {
    store.setEnsureLoaded(() => setLessonTabOpened(true));
  }, [store]);

  const { subjectData, classesData, teacherData, roomsData, subjectContainerData } = useLoadBasicData({
    pause: !versionUuid || !lessonTabOpened,
  });

  const [{ data: lessonData }, reexecuteLessonData] = useTimetableVersionLessonDataQuery({
    variables: { versionUuid: versionUuid ?? '' },
    requestPolicy: 'network-only',
    pause: !versionUuid || !lessonTabOpened,
  });

  // Refresh-Weg analog zum PinboardStore: nach Änderungen außerhalb von GraphQL (ASC-Import)
  // muss die Lesson-Query neu laufen, sonst zeigt die Liste gelöschte Unterrichte weiter an.
  useEffect(() => {
    store.setReloadLessons(() => reexecuteLessonData({ requestPolicy: 'network-only' }));
  }, [store, reexecuteLessonData]);

  const entityMaps = useMemo(() => {
    const subjectByUuid = new Map<string, { uuid: string; name: string; shortName: string }>([
      ...(subjectData?.subjects.map((s) => [s.uuid, { uuid: s.uuid, name: s.name, shortName: s.shortName }] as const) ??
        []),
      ...(subjectContainerData?.subjectContainers.map(
        (s) => [s.uuid, { uuid: s.uuid, name: s.name, shortName: s.shortName }] as const,
      ) ?? []),
    ]);
    const classsByUuid = new Map(classesData?.classes.map((c) => [c.uuid, c]) ?? []);
    const teacherByUuid = new Map(teacherData?.people.map((t) => [t.uuid, t]) ?? []);
    const roomByUuid = new Map(roomsData?.rooms.map((r) => [r.uuid, r]) ?? []);

    return { subjectByUuid, classsByUuid, teacherByUuid, roomByUuid };
  }, [subjectData, subjectContainerData, classesData, teacherData, roomsData, lessonData]);

  // entityMaps über eine Ref lesen, damit die Store-Befüllung nicht bei jeder
  // (oft referenz-identischen) Neuberechnung der BasicData erneut läuft.
  const entityMapsRef = useRef(entityMaps);
  entityMapsRef.current = entityMaps;

  // Der Store wird nur (neu) befüllt, wenn die Lesson-Query frische Daten liefert
  // oder die BasicData erstmals bereitstehen – NICHT bei jeder BasicData-Neuemission.
  // Sonst würde ein lokal (optimistisch) entferntes Lesson aus dem noch stale
  // lessonData wieder auferstehen (Zeile taucht ohne Kärtchen wieder auf, bis zum Reload).
  const basicDataReady = Boolean(subjectData && classesData && teacherData && roomsData);

  // biome-ignore lint/correctness/useExhaustiveDependencies: BasicData bewusst nur via basicDataReady/Ref, nicht als Trigger (siehe Kommentar oben)
  useEffect(() => {
    if (!lessonData) return;

    const rows = lessonData.timetableVersionLessonData.lessons;
    const lessonsData = versionLessonRowToTableType(rows, entityMapsRef.current);
    store.setLessons(lessonsData);
    store.setBasicData({
      subjects: subjectData?.subjects,
      classes: classesData?.classes,
      teachers: teacherData?.people,
      rooms: roomsData?.rooms,
    });
  }, [store, lessonData, basicDataReady]);

  return <LessonStoreContext.Provider value={store}>{children}</LessonStoreContext.Provider>;
};

export const useLessonStore = () => useContext(LessonStoreContext);
